import React, { useState, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';
import api from '../services/api';
import {
  Box, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Alert,
  CircularProgress, MenuItem, Select, InputLabel, FormControl, FormLabel,
  RadioGroup, FormControlLabel, Radio, Autocomplete, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { formatDateOnly } from '../utils/dateTime';

const TIPO_MANUTENCAO_LABELS = {
  corretiva: 'Corretiva',
  preventiva: 'Preventiva',
  recorrente: 'Recorrente',
};

function ManutencaoManager() {
  const [manutencoes, setManutencoes] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const [editingId, setEditingId] = useState(null);
  const [equipamento_id, setEquipamentoId] = useState('');
  const [tipo_manutencao, setTipoManutencao] = useState('preventiva');
  const [descricao, setDescricao] = useState('');
  const [data_execucao, setDataExecucao] = useState('');
  const [data_proxima_manutencao, setDataProxima] = useState('');

  const [pessoaOptions, setPessoaOptions] = useState([]);
  const [selectedTecnico, setSelectedTecnico] = useState(null);
  const [loadingPessoas, setLoadingPessoas] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const debouncedFetchPessoas = useMemo(
    () => debounce((searchTerm) => {
      setLoadingPessoas(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then((response) => {
          const list = response.data?.data || response.data;
          setPessoaOptions(Array.isArray(list) ? list : []);
        })
        .catch((err) => console.error('Erro ao buscar pessoas:', err))
        .finally(() => setLoadingPessoas(false));
    }, 500),
    []
  );

  useEffect(() => () => {
    debouncedFetchPessoas.cancel();
  }, [debouncedFetchPessoas]);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get('/manutencoes?limit=200'),
      api.get('/equipamentos?lista_leve=true'),
    ])
      .then(([mntResponse, eqResponse]) => {
        setManutencoes(mntResponse.data?.data || []);
        setEquipamentos(eqResponse.data || []);
      })
      .catch((err) => {
        console.error('Erro ao carregar manutenções:', err);
        setError('Não foi possível carregar as manutenções.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchTecnicoById = (id) => {
    if (!id) {
      setSelectedTecnico(null);
      return;
    }
    api.get(`/pessoas?id=${id}`)
      .then((response) => {
        const item = (response.data?.data || response.data)[0];
        if (item) {
          setSelectedTecnico(item);
          setPessoaOptions([item]);
        } else {
          setSelectedTecnico(null);
        }
      })
      .catch((err) => console.error('Erro ao buscar técnico por ID:', err));
  };

  const resetForm = () => {
    setEditingId(null);
    setEquipamentoId('');
    setTipoManutencao('preventiva');
    setDescricao('');
    setDataExecucao('');
    setDataProxima('');
    setSelectedTecnico(null);
    setPessoaOptions([]);
    setDialogOpen(false);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEquipamentoId(item.equipamento_id);
    setTipoManutencao(item.tipo_manutencao || 'preventiva');
    setDescricao(item.descricao || '');
    setDataExecucao(item.data_execucao || '');
    setDataProxima(item.data_proxima_manutencao || '');
    fetchTecnicoById(item.tecnico_pessoa_id);
    setDialogOpen(true);
  };

  const handleNew = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta manutenção?')) return;
    api.delete(`/manutencoes/${id}`)
      .then(() => fetchAll())
      .catch((err) => {
        console.error('Erro ao deletar manutenção:', err);
        setError(err.response?.data || 'Erro ao deletar manutenção.');
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError('');

    const payload = {
      equipamento_id,
      tipo_manutencao,
      descricao: descricao.trim(),
      tecnico_pessoa_id: selectedTecnico ? selectedTecnico.id : null,
      data_execucao,
      data_proxima_manutencao: data_proxima_manutencao || null,
    };

    const request = editingId
      ? api.put(`/manutencoes/${editingId}`, payload)
      : api.post('/manutencoes', payload);

    request
      .then(() => {
        resetForm();
        fetchAll();
      })
      .catch((err) => {
        console.error('Erro ao salvar manutenção:', err);
        setError(err.response?.data || 'Erro ao salvar manutenção.');
      })
      .finally(() => {
        submittingRef.current = false;
        setSubmitting(false);
      });
  };

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Nova manutenção
        </Button>
      </Box>

      <Dialog open={dialogOpen} onClose={resetForm} fullWidth maxWidth="md">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>{editingId ? 'Editar Manutenção' : 'Registro de Manutenção'}</DialogTitle>
          <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>Equipamento</InputLabel>
              <Select
                label="Equipamento"
                value={equipamento_id}
                onChange={(e) => setEquipamentoId(e.target.value)}
              >
                {equipamentos.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.nome}
                    {item.local_nome ? ` (${item.local_nome})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <FormLabel component="legend" sx={{ mb: 0.5 }}>Tipo de Manutenção</FormLabel>
              <RadioGroup
                row
                value={tipo_manutencao}
                onChange={(e) => setTipoManutencao(e.target.value)}
              >
                {Object.entries(TIPO_MANUTENCAO_LABELS).map(([value, label]) => (
                  <FormControlLabel key={value} value={value} control={<Radio />} label={label} />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField
              label="Descrição da Manutenção"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              fullWidth
              required
              multiline
              minRows={2}
              placeholder="Descreva o que foi realizado"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              options={pessoaOptions}
              getOptionLabel={(option) => option.nome || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedTecnico}
              onChange={(event, newValue) => setSelectedTecnico(newValue)}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  if (newInputValue) debouncedFetchPessoas(newInputValue);
                  else setPessoaOptions([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Técnico (executor)"
                  required
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              label="Data de Execução"
              type="date"
              value={data_execucao}
              onChange={(e) => setDataExecucao(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              helperText="Permite cadastro retroativo"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              label="Próxima Manutenção (opcional)"
              type="date"
              value={data_proxima_manutencao}
              onChange={(e) => setDataProxima(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetForm} disabled={submitting}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data Execução</TableCell>
              <TableCell>Equipamento</TableCell>
              <TableCell>Tipo Mnt.</TableCell>
              <TableCell>Técnico</TableCell>
              <TableCell>Próxima</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : manutencoes.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">Nenhuma manutenção registrada.</TableCell></TableRow>
            ) : manutencoes.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{formatDateOnly(item.data_execucao)}</TableCell>
                <TableCell>{item.equipamento_nome || '-'}</TableCell>
                <TableCell>{TIPO_MANUTENCAO_LABELS[item.tipo_manutencao] || item.tipo_manutencao}</TableCell>
                <TableCell>{item.tecnico_nome || '-'}</TableCell>
                <TableCell>{formatDateOnly(item.data_proxima_manutencao)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" aria-label="editar" onClick={() => handleEdit(item)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" aria-label="deletar" onClick={() => handleDelete(item.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ManutencaoManager;
